import type { Resource, ResourceInput, Instance, InstanceInput } from '@detect/shared';
import { api, withTenant, type PaginatedResult } from './api';
import type { WithId } from './types';

/** Resources API (M4 surface). List honors the API's one-equality-
 *  filter rule: pass kind OR collectorId, never both. */
export const resourceService = {
  list: (
    tenantId: string,
    params: {
      kind?: string;
      collectorId?: string;
      limit?: number;
      startAfter?: string;
    } = {}
  ): Promise<PaginatedResult<WithId<Resource>>> =>
    api.getPaginated<WithId<Resource>>(withTenant('/resources', tenantId, params)),

  get: (tenantId: string, id: string): Promise<WithId<Resource>> =>
    api.get<WithId<Resource>>(withTenant(`/resources/${id}`, tenantId)),

  create: (tenantId: string, input: ResourceInput): Promise<WithId<Resource>> =>
    api.post<WithId<Resource>>(withTenant('/resources', tenantId), input),

  update: (
    tenantId: string,
    id: string,
    patch: Record<string, unknown>
  ): Promise<WithId<Resource>> =>
    api.patch<WithId<Resource>>(withTenant(`/resources/${id}`, tenantId), patch),

  remove: (tenantId: string, id: string): Promise<void> =>
    api.delete(withTenant(`/resources/${id}`, tenantId)),

  listInstances: (
    tenantId: string,
    resourceId: string,
    params: { limit?: number; startAfter?: string } = {}
  ): Promise<PaginatedResult<WithId<Instance>>> =>
    api.getPaginated<WithId<Instance>>(
      withTenant(`/resources/${resourceId}/instances`, tenantId, params)
    ),

  addInstance: (
    tenantId: string,
    resourceId: string,
    input: Omit<InstanceInput, 'resourceId'>
  ): Promise<WithId<Instance>> =>
    api.post<WithId<Instance>>(
      withTenant(`/resources/${resourceId}/instances`, tenantId),
      { ...input, resourceId }
    ),

  /** Test-now (#24): immediate website check across configured regions. */
  checkNow: (
    tenantId: string,
    id: string
  ): Promise<{
    rollup: Record<string, number | undefined>;
    regions: Record<string, { available: number; response_ms: number; status_code: number }>;
    probersUnavailable: boolean;
  }> => api.post(withTenant(`/resources/${id}/check-now`, tenantId)),

  retireInstance: (
    tenantId: string,
    resourceId: string,
    instanceId: string
  ): Promise<WithId<Instance>> =>
    api.post<WithId<Instance>>(
      withTenant(`/resources/${resourceId}/instances/${instanceId}/retire`, tenantId)
    ),
};
